import { zodResolver } from '@hookform/resolvers/zod';
import type {
    DataStreamBaseTileConfig,
    DataStreamDefinitionPartial,
    StreamDataColumnDefinition
} from '@squaredup/data-streams';
import type { ConfigId, Serialised } from '@squaredup/ids';
import { supportedTimeframes, type TimeframeEnumValue } from '@squaredup/timeframes';
import { isDefined, TemplateSchema, type MatchSpec } from '@squaredup/utilities';
import buildErrorMessage from 'components/forms/input/buildErrorMessage';
import type { DataStreamDefinitionEntity } from 'dynamo-wrapper';
import { differenceBy } from 'lodash';
import { dataStreamDefinitionQueryKeys } from 'queries/queryKeys/dataStreamDefinitionKeys';
import { useForm, type DefaultValues } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { Create, type DataStream } from 'services/DataStreamDefinitionService';
import { z } from 'zod';

const dataStreamFormDataSchema = z.object({
    displayName: z
        .string()
        .trim()
        .min(3, buildErrorMessage('minLength', 'displayName', { minLength: 3 }))
        .max(128, buildErrorMessage('maxLength', 'Data stream name', { maxLength: 128 })),
    includeMetadata: z.boolean(),
    includeShaping: z.boolean(),
    restrictTimeframes: z.boolean(),
    allowedTimeframes: z.array(z.string()).optional(),
    includeUI: z.boolean(),
    uiConfig: TemplateSchema.optional(),
    uiConfigMapping: z.record(z.any()).optional()
});

export type DataStreamSchemaFormData = z.infer<typeof dataStreamFormDataSchema>;

interface UseSaveAsDataStreamMutationArgs {
    onSuccess?: (dataStream: Serialised<DataStreamDefinitionEntity>) => void;
}

interface UseSaveAsDataStreamFormArgs {
    defaultValues?: DefaultValues<DataStreamSchemaFormData>;
}

/**
 * Get all supported timeframes for a data stream.
 */
export const getSupportedTimeframes = (dataStreamDefinition: DataStream['definition']): TimeframeEnumValue[] => {
    if (dataStreamDefinition.timeframes === false) {
        return [];
    }

    if (Array.isArray(dataStreamDefinition.timeframes)) {
        return dataStreamDefinition.timeframes;
    }

    return supportedTimeframes;
};

export const useSaveAsDataStreamForm = ({ defaultValues }: UseSaveAsDataStreamFormArgs) => {
    const form = useForm<DataStreamSchemaFormData>({
        defaultValues,
        mode: 'all',
        reValidateMode: 'onChange',
        resolver: zodResolver(dataStreamFormDataSchema)
    });

    return { form };
};

export const useSaveAsDataStreamMutation = ({ onSuccess }: UseSaveAsDataStreamMutationArgs) => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: Create,
        onSuccess: async (createdDatastream) => {
            await queryClient.invalidateQueries(dataStreamDefinitionQueryKeys.all);

            onSuccess?.(createdDatastream);
        }
    });

    return mutation;
};

const addConfigIdMatchCriteria = (matches: MatchSpec, configId: ConfigId['value']): MatchSpec => {
    if (Array.isArray(matches)) {
        return matches.map((match) => addConfigIdMatchCriteria(match, configId)) as MatchSpec;
    }

    if (
        typeof matches === 'string' ||
        !('sourceType' in matches) ||
        typeof matches.sourceType === 'string' ||
        matches.sourceType.type !== 'equals' ||
        matches.sourceType.value !== 'squaredup/data-source'
    ) {
        // We need a global data stream for this to work, other streams won't have a config Id as a source Id.
        throw new Error(
            'Cannot add configId match criteria to non-data source matches (is this a global data stream?).'
        );
    }

    return {
        ...matches,
        sourceId: { type: 'equals', value: configId }
    };
};

/**
 * Convert the form data from the "Save As Data Stream" form into a data stream definition which can be saved.
 * @param baseDataStream The definition of the data stream that the saved data stream will be based on.
 * @param dataStreamConfig The data stream configuration of the stream being saved.
 * @param formData The form data from the "Save As Data Stream" form.
 * @param configId The Id of the plugin config this data stream should be saved against.
 * The data stream will only work with this config.
 * @returns A data stream definition that can be saved.
 */
export const saveAsDataStreamFormToDataStreamDefinition = (
    baseDataStream: Serialised<DataStreamDefinitionEntity>,
    dataStreamConfig: DataStreamBaseTileConfig['dataStream'],
    formData: DataStreamSchemaFormData,
    configId: ConfigId['value']
): DataStreamDefinitionPartial => {
    const baseDataStreamMetadata = baseDataStream.definition.metadata ?? [];
    const metadataOverrides = dataStreamConfig?.metadata?.filter(isDefined) ?? [];

    /**
     * Timeframes selected in the UI and supported by the base data stream.
     */
    const filteredTimeframes = getSupportedTimeframes(baseDataStream.definition).filter(
        (timeframe) => !formData.restrictTimeframes || formData.allowedTimeframes?.includes(timeframe)
    );

    // Translate 'none' to false, so that the data stream definition can be saved with no timeframes.
    const timeframes =
        filteredTimeframes.length === 0 || filteredTimeframes.every((t) => t === 'none') ? false : filteredTimeframes;

    const shaping = {
        filter: dataStreamConfig?.filter,
        group: dataStreamConfig?.group,
        sort: dataStreamConfig?.sort
    };

    const definition: DataStreamDefinitionPartial = {
        ...baseDataStream.definition,
        timeframes,
        matches: addConfigIdMatchCriteria(baseDataStream.definition.matches ?? {}, configId),
        metadata:
            ((formData.includeMetadata
                ? [
                      ...(baseDataStream.definition.metadata?.map((column) => ({
                          ...column,
                          ...metadataOverrides.find((o) => 'name' in o && 'name' in column && o.name === column.name)
                      })) ?? []),
                      ...differenceBy(metadataOverrides, baseDataStreamMetadata, 'name')
                  ]
                : baseDataStream.definition.metadata
            )?.filter(Boolean) as StreamDataColumnDefinition[]) ?? [],
        defaultShaping: formData.includeShaping
            ? (shaping as DataStreamDefinitionPartial['defaultShaping'])
            : undefined,
        uiConfigMapping: formData.includeUI && formData.uiConfigMapping ? formData.uiConfigMapping : undefined
    };

    return definition;
};
